/**
 * Onboarding Flow Hook
 *
 * Manages state and logic for the authentication onboarding flow.
 */

import { useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toJS } from "mobx";
import { userContext } from "../../../stores/users/userContext";
import { setLocalStorage } from "../../../utils";
import { setCookie } from "../../../utils/cookie";
import {
  sendVerificationCode,
  verifyOTPCode,
  authenticateWithGoogle,
  ensureCsrfCookie,
  isValidEmail,
} from "../../../api";
import { User } from "../../../api/types";
import { parseBackendError } from "../../../utils/errorUtils";
import { ERROR_MESSAGES } from "../constants/onboardingText";

export type OnboardingStep = "email" | "otp";

export interface OnboardingState {
  step: OnboardingStep;
  email: string;
  loading: boolean;
  error: string | null;
  resendCooldown: number;
}

export interface OnboardingActions {
  handleEmailSubmit: (email: string, csrfToken: string) => Promise<void>;
  handleOTPSubmit: (code: string, csrfToken: string) => Promise<void>;
  handleResendCode: (email: string, csrfToken: string) => Promise<void>;
  handleGoogleSuccess: (credentialResponse: {
    credential: string;
  }) => Promise<void>;
  handleGoogleError: () => void;
  handleBack: () => void;
  clearError: () => void;
}

export interface UseOnboardingFlowReturn
  extends OnboardingState, OnboardingActions {}

const TOKEN_EXPIRATION_DAYS = 30;
const RESEND_COOLDOWN_SECONDS = 30;
const COOLDOWN_INTERVAL_MS = 1000;

/**
 * onAuthed — a host flow (the flagship upload gate) passes this to keep control after sign-in.
 * Navigating to "/" would unmount the uploader and drop the File the visitor already chose,
 * making them start over — which is exactly the moment you lose them.
 */
export function useOnboardingFlow(
  onAuthed?: () => void,
): UseOnboardingFlowReturn {
  const userStore = useContext(userContext);
  const navigate = useNavigate();

  const [step, setStep] = useState<OnboardingStep>("email");
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState<number>(0);

  const handleAuthSuccess = useCallback(
    (token: string, user: User) => {
      userStore.setPhase("logged");
      userStore.setToken(token);
      userStore.setUser(toJS(user));
      setLocalStorage("token", token);
      setCookie("token", token, TOKEN_EXPIRATION_DAYS);
      if (onAuthed) {
        onAuthed();
        return;
      }
      navigate("/");
    },
    [userStore, navigate, onAuthed],
  );

  const startResendCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);

    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, COOLDOWN_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  const handleEmailSubmit = useCallback(
    async (emailValue: string, csrfToken: string) => {
      setLoading(true);
      setError(null);

      try {
        const trimmedEmail = emailValue.trim();

        // Client-side validation
        if (!isValidEmail(trimmedEmail)) {
          setError(ERROR_MESSAGES.EMAIL_INVALID);
          setLoading(false);
          return;
        }

        const { response, data } = await sendVerificationCode(
          trimmedEmail,
          csrfToken,
        );

        if (response.status === 200) {
          setEmail(trimmedEmail);
          setStep("otp");
          userStore.setPhase("get_pass");
        } else if (response.status === 422) {
          setError(
            parseBackendError(data, "email", ERROR_MESSAGES.EMAIL_INVALID),
          );
        } else {
          setError(
            parseBackendError(data, "email", ERROR_MESSAGES.NETWORK_ERROR),
          );
        }
      } catch (err) {
        setError(ERROR_MESSAGES.NETWORK_ERROR);
        console.error("Email submission error:", err);
      } finally {
        setLoading(false);
      }
    },
    [userStore],
  );

  const handleOTPSubmit = useCallback(
    async (code: string, csrfToken: string) => {
      setLoading(true);
      setError(null);

      try {
        const { response, data } = await verifyOTPCode(email, code, csrfToken);

        if (response.status === 200) {
          handleAuthSuccess(data.token, data.user);
        } else if (response.status === 422) {
          const message = data.message || "";
          if (message.includes("expired")) {
            setError(ERROR_MESSAGES.OTP_EXPIRED);
          } else if (message.includes("attempts")) {
            setError(ERROR_MESSAGES.OTP_TOO_MANY_ATTEMPTS);
          } else {
            setError(ERROR_MESSAGES.OTP_INVALID);
          }
        } else {
          setError(data.message || ERROR_MESSAGES.VERIFICATION_FAILED);
        }
      } catch (err) {
        setError(ERROR_MESSAGES.NETWORK_ERROR);
        console.error("OTP submission error:", err);
      } finally {
        setLoading(false);
      }
    },
    [email, handleAuthSuccess],
  );

  const handleResendCode = useCallback(
    async (emailValue: string, csrfToken: string) => {
      if (resendCooldown > 0) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { response, data } = await sendVerificationCode(
          emailValue,
          csrfToken,
        );

        if (response.status === 200) {
          setError(null);
          startResendCooldown();
        } else {
          const message = data.message || "";
          if (message.includes("limit") || message.includes("cooldown")) {
            startResendCooldown();
          } else {
            setError(message || ERROR_MESSAGES.RESEND_FAILED);
          }
        }
      } catch (err) {
        setError(ERROR_MESSAGES.NETWORK_ERROR);
        console.error("Resend code error:", err);
      } finally {
        setLoading(false);
      }
    },
    [resendCooldown, startResendCooldown],
  );

  const handleGoogleSuccess = useCallback(
    async (credentialResponse: { credential: string }) => {
      setLoading(true);
      setError(null);

      try {
        const { response, data } = await authenticateWithGoogle(
          credentialResponse.credential,
        );

        if (response.status === 200) {
          handleAuthSuccess(data.token, data.user);
          await ensureCsrfCookie();
        } else {
          setError(data.message || ERROR_MESSAGES.GOOGLE_LOGIN_FAILED);
        }
      } catch (err) {
        setError(ERROR_MESSAGES.NETWORK_ERROR);
      } finally {
        setLoading(false);
      }
    },
    [handleAuthSuccess],
  );

  const handleGoogleError = useCallback(() => {
    setError(ERROR_MESSAGES.GOOGLE_LOGIN_UNSUCCESSFUL);
  }, []);

  const handleBack = useCallback(() => {
    setStep("email");
    setError(null);
    setResendCooldown(0);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    step,
    email,
    loading,
    error,
    resendCooldown,
    handleEmailSubmit,
    handleOTPSubmit,
    handleResendCode,
    handleGoogleSuccess,
    handleGoogleError,
    handleBack,
    clearError,
  };
}

export default useOnboardingFlow;
